// ** React Imports

// ** Next Imports
import type { NextPage } from 'next'
import type { AppProps } from 'next/app'
import Head from 'next/head'
import { Router } from 'next/router'

// ** Loader Import
import NProgress from 'nprogress'

// ** Emotion Imports
import type { EmotionCache } from '@emotion/cache'
import { CacheProvider } from '@emotion/react'

// ** Config Imports
import themeConfig from 'src/@core/configs/themeConfig'

// ** Component Imports
import ThemeComponent from 'src/@core/theme/ThemeComponent'

// ** Contexts
import { SettingsConsumer, SettingsProvider } from 'src/@core/context/settingsContext'
import { AuthProvider } from 'src/lib/contexts/AuthContext'
import { MerchantProvider } from 'src/lib/contexts/MerchantContext'
import { CheckoutUIProvider } from 'src/lib/contexts/CheckoutUIContext'
// import { ConfirmProvider } from 'material-ui-confirm';

// ** Utils Imports
import { createEmotionCache } from 'src/@core/utils/create-emotion-cache'

// ** React Perfect Scrollbar Style
import 'react-perfect-scrollbar/dist/css/styles.css'

// ** Global css styles
import { StyledEngineProvider } from '@mui/styled-engine'
import '@shopflo/ui/styles.css'
import '../styles/globals.css'

import { LicenseInfo } from '@mui/x-license-pro'
import { Toaster } from 'react-hot-toast'
import { FeatureProfileProvider } from 'src/lib/contexts/FeatureProfileContext'
import { FilterProvider } from 'src/lib/contexts/FilterContext'
import { IntegrationsProvider } from 'src/lib/contexts/IntegrationsContext'
import { LoaderProvider } from 'src/lib/contexts/LoaderContext'
import { OnboardingProvider } from 'src/lib/contexts/OnboardingContext'
import OrganisationProvider from 'src/lib/contexts/OrganisationContext'
import { TemporaryProvider } from 'src/lib/contexts/TemporaryContext'
import { UserProvider } from 'src/lib/contexts/UserContext'
import { TooltipProvider } from '@shopflo/ui'
import UserLayoutV2 from 'src/layouts/UserLayoutV2'
import MobileLayout from 'src/layouts/MobileLayout'
import { NavigationProvider } from 'src/lib/hooks/useNavigation'
import { MobileProvider } from 'src/lib/contexts/MobileContext'
import { useRouter } from 'next/router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { CopilotProvider } from 'src/lib/copilot/CopilotProvider'
import CopilotTrigger from 'src/components/copilot/CopilotTrigger'
import dynamic from 'next/dynamic'
import { useUser } from 'src/lib/contexts/UserContext'
import { hasPermissionToTab } from 'src/lib/utils/navigation-rbac'
import { permissionType } from 'src/lib/utils/constants'

// Deferred chunk: keeps react-markdown/highlight.js/chart.js out of the main
// bundle — it loads only for users the CopilotShell below actually mounts it for.
const ChatWidget = dynamic(() => import('src/components/copilot/ChatWidget'), { ssr: false })

// ** Extend App Props with Emotion
type ExtendedAppProps = AppProps & {
  Component: NextPage
  emotionCache: EmotionCache
}

const clientSideEmotionCache = createEmotionCache()

const copilotQueryClient = new QueryClient({
  defaultOptions: { queries: { retry: false, staleTime: 30_000 } }
})

/** v1: Home-only copilot (/home + nav tab). Set true for v2 floating widget + trigger. */
const COPILOT_FLOATING_WIDGET_ENABLED = false

/**
 * Mounts copilot context for TAB_COPILOT users on non-mobile routes.
 * v1: provider only (Home nav + /home surface). v2: also floating widget + trigger.
 * Logged-out pages (/auth/*) have no user, so no chrome renders.
 */
const CopilotShell = ({ children }: { children: React.ReactNode }) => {
  const router = useRouter()
  const { user } = useUser()
  const isMobileRoute = router.pathname.startsWith('/mobile')
  const hasCopilot = hasPermissionToTab(user, permissionType.TAB_COPILOT)

  if (!user || !hasCopilot || isMobileRoute) return <>{children}</>

  return (
    <QueryClientProvider client={copilotQueryClient}>
      <CopilotProvider>
        {children}
        {COPILOT_FLOATING_WIDGET_ENABLED ? (
          <>
            <ChatWidget />
            <CopilotTrigger />
          </>
        ) : null}
      </CopilotProvider>
    </QueryClientProvider>
  )
}

// ** Pace Loader
if (themeConfig.routingLoader) {
  Router.events.on('routeChangeStart', () => {
    NProgress.start()
  })
  Router.events.on('routeChangeError', () => {
    NProgress.done()
  })
  Router.events.on('routeChangeComplete', () => {
    NProgress.done()
  })
}

LicenseInfo.setLicenseKey(
  '9dc2872f65f5defacdf7324cf0c3e680Tz01MjIwMSxFPTE2OTcwMjA0MTEyMzYsUz1wcm8sTE09c3Vic2NyaXB0aW9uLEtWPTI='
)

/**
 * Get the appropriate layout based on the current route
 * - Mobile routes (/mobile/*) use MobileLayout
 * - All other routes use the component's getLayout or default to UserLayoutV2
 */
const getLayoutForRoute = (Component: NextPage, pathname: string) => {
  const isMobileRoute = pathname.startsWith('/mobile')

  if (isMobileRoute) {
    return (page: React.ReactNode) => <MobileLayout>{page}</MobileLayout>
  }

  return Component.getLayout ?? ((page: React.ReactNode) => <UserLayoutV2>{page}</UserLayoutV2>)
}

// ** Configure JSS & ClassName
const App = (props: ExtendedAppProps) => {
  const { Component, emotionCache = clientSideEmotionCache, pageProps } = props
  const router = useRouter()
  const getLayout = getLayoutForRoute(Component, router.pathname)

  return (
    <LoaderProvider>
      <AuthProvider>
        <MobileProvider>
          <NavigationProvider>
            <OrganisationProvider>
              <OnboardingProvider>
                <UserProvider>
                  <FilterProvider>
                    <TemporaryProvider>
                      <MerchantProvider>
                        <CheckoutUIProvider>
                          <IntegrationsProvider>
                            <CacheProvider value={emotionCache}>
                              <Head>
                                <title>{`Dashboard · ${themeConfig.templateName}`}</title>
                                <meta
                                  name='description'
                                  content={`${themeConfig.templateName} – Shopflo Merchant Dashboard`}
                                />
                                <meta name='keywords' content='Shopflo, dashboard' />
                                <meta name='viewport' content='initial-scale=1, width=device-width' />
                              </Head>

                              <SettingsProvider>
                                <StyledEngineProvider>
                                  <SettingsConsumer>
                                    {({ settings }) => (
                                      <TooltipProvider>
                                        <ThemeComponent settings={settings}>
                                          <CopilotShell>{getLayout(<Component {...pageProps} />)}</CopilotShell>
                                          <Toaster />
                                        </ThemeComponent>
                                      </TooltipProvider>
                                    )}
                                  </SettingsConsumer>
                                </StyledEngineProvider>
                              </SettingsProvider>
                            </CacheProvider>
                          </IntegrationsProvider>
                        </CheckoutUIProvider>
                      </MerchantProvider>
                    </TemporaryProvider>
                  </FilterProvider>
                </UserProvider>
              </OnboardingProvider>
            </OrganisationProvider>
          </NavigationProvider>
        </MobileProvider>
      </AuthProvider>
    </LoaderProvider>
  )
}

export default App
